import { ArrowLeft, Check, ChevronDown, KeyRound, Mail, UserPlus } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { Button, cx, Field, Input } from "../components/ui.js";
import { authClient, signIn } from "../lib/auth.js";

type Mode = "password" | "magic" | "signup" | "forgot";

function BrandIllustration() {
  return (
    <svg viewBox="0 0 320 220" className="w-full max-w-sm" aria-hidden fill="none">
      <rect x="40" y="24" width="240" height="172" rx="16" fill="rgb(255 255 255 / 0.08)" stroke="rgb(255 255 255 / 0.18)" />
      <rect x="40" y="24" width="240" height="36" rx="16" fill="rgb(255 255 255 / 0.06)" />
      <circle cx="62" cy="42" r="5" fill="rgb(255 255 255 / 0.35)" />
      <rect x="76" y="38" width="70" height="8" rx="4" fill="rgb(255 255 255 / 0.35)" />
      {[0, 1, 2, 3].map((i) => (
        <g key={i} transform={`translate(0 ${80 + i * 28})`}>
          <circle cx="66" cy="0" r="8" stroke="#fff" strokeOpacity={i === 0 ? 1 : 0.45} strokeWidth="2" fill={i === 0 ? "#fff" : "none"} />
          {i === 0 && <path d="M62 0l3 3 5-6" stroke="#c53a2c" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />}
          <rect x="84" y="-4" width={[120, 150, 96, 132][i]} height="8" rx="4" fill="#fff" fillOpacity={i === 0 ? 0.3 : 0.6} />
          <rect x={[212, 242, 188, 224][i]} y="-4" width="26" height="8" rx="4" fill="#fff" fillOpacity="0.22" />
        </g>
      ))}
    </svg>
  );
}

function Logo({ light }: { light?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <img src="/icon.svg" alt="" className={cx("h-9 w-9 rounded-[10px]", light && "ring-1 ring-white/30")} />
      <span className={cx("text-subtitle font-semibold", light ? "text-white" : "text-text")}>Todoer</span>
    </div>
  );
}

/** Split layout on desktop (brand panel + form), single centred column on phone. */
function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh bg-surface">
      <aside className="relative hidden w-[44%] max-w-[640px] flex-col justify-between overflow-hidden bg-brand-strong p-12 text-white lg:flex">
        <div aria-hidden className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <Logo light />
        <div>
          <h2 className="max-w-md text-title font-semibold text-white">Everything your team has to do, in one calm list.</h2>
          <p className="mt-3 max-w-md text-body text-white/80">Capture tasks in plain words, plan your day, and share projects with your team. Works offline on every device.</p>
          <div className="mt-10"><BrandIllustration /></div>
        </div>
        <p className="text-meta text-white/70">Private workspace · todoer.uk</p>
      </aside>
      <main className="flex flex-1 items-center justify-center px-4 py-10 pt-safe">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden"><Logo /></div>
          {children}
        </div>
      </main>
    </div>
  );
}

export function Login() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<Mode>("password");
  const [signupOpen, setSignupOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [others, setOthers] = useState(false);

  useEffect(() => {
    fetch("/api/setup").then((r) => r.json()).then((d: { signupOpen: boolean }) => {
      setSignupOpen(d.signupOpen);
      if (d.signupOpen) setMode("signup");
    }).catch(() => {});
  }, []);

  const switchMode = (m: Mode) => { setMode(m); setError(null); setSent(null); setOthers(false); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setSent(null);
    try {
      if (mode === "password") {
        const res = await signIn.email({ email, password });
        if (res.error) setError(res.error.message ?? "Sign in failed");
      } else if (mode === "signup") {
        const res = await authClient.signUp.email({ name, email, password });
        if (res.error) setError(res.error.message ?? "Could not create account");
      } else if (mode === "magic") {
        const res = await authClient.signIn.magicLink({ email, callbackURL: "/today" });
        if (res.error) setError(res.error.message ?? "Could not send link");
        else setSent("Check your email for a sign-in link.");
      } else {
        const res = await authClient.requestPasswordReset({ email, redirectTo: "/reset-password" });
        if (res.error) setError(res.error.message ?? "Could not send the reset email");
        else setSent("If that address has an account, a reset link is on its way.");
      }
    } finally {
      setBusy(false);
    }
  };

  const heading = { password: "Sign in", signup: "Create the first account", magic: "Sign in with a link", forgot: "Reset your password" }[mode];
  const sub = {
    password: "Welcome back to your team workspace.",
    signup: "You'll be the admin of this workspace.",
    magic: "We'll email you a link that signs you in.",
    forgot: "Enter your email and we'll send you a reset link.",
  }[mode];
  const alternatives: { mode: Mode; label: string; icon: typeof Mail }[] = [
    ...(mode !== "password" ? [{ mode: "password" as Mode, label: "Sign in with a password", icon: KeyRound }] : []),
    ...(mode !== "magic" ? [{ mode: "magic" as Mode, label: "Email me a sign-in link", icon: Mail }] : []),
    ...(signupOpen && mode !== "signup" ? [{ mode: "signup" as Mode, label: "Create an account", icon: UserPlus }] : []),
  ];

  return (
    <AuthShell>
      <h1 className="text-title font-semibold">{heading}</h1>
      <p className="mt-1 text-body text-text-muted">{sub}</p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        {mode === "signup" && (
          <Field label="Name" htmlFor="li-name">
            <Input id="li-name" autoFocus required value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
          </Field>
        )}
        <Field label="Email" htmlFor="li-email">
          <Input id="li-email" key={mode} autoFocus={mode !== "signup"} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        </Field>
        {(mode === "password" || mode === "signup") && (
          <div>
            <div className="mb-1.5 flex items-center">
              <label htmlFor="li-pass" className="text-meta font-medium text-text-muted">Password</label>
              {mode === "password" && (
                <button type="button" onClick={() => switchMode("forgot")} className="ml-auto rounded px-1 text-meta font-medium text-brand-text hover:underline phone:py-2">Forgot password?</button>
              )}
            </div>
            <Input id="li-pass" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === "signup" ? "new-password" : "current-password"} />
            {mode === "signup" && <p className="mt-1.5 text-meta text-text-faint">At least 8 characters.</p>}
          </div>
        )}
        {error && <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-body text-danger">{error}</p>}
        {sent && <p role="status" className="flex items-start gap-2 rounded-control bg-success-soft px-3 py-2 text-body text-success"><Check size={16} className="mt-0.5" aria-hidden />{sent}</p>}
        <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={busy}>
          {mode === "password" ? "Sign in" : mode === "signup" ? "Create account" : mode === "magic" ? "Email me a link" : "Send reset link"}
        </Button>
      </form>

      {mode === "forgot" ? (
        <button type="button" onClick={() => switchMode("password")} className="mt-6 inline-flex h-10 items-center gap-1.5 text-body font-medium text-text-muted hover:text-text">
          <ArrowLeft size={16} aria-hidden />Back to sign in
        </button>
      ) : alternatives.length > 0 && (
        <div className="mt-6 border-t border-border pt-4">
          <button type="button" onClick={() => setOthers((o) => !o)} aria-expanded={others}
            className="flex h-10 w-full items-center justify-center gap-1.5 text-body font-medium text-text-muted hover:text-text">
            Other ways to sign in <ChevronDown size={16} aria-hidden className={cx("transition-transform", others && "rotate-180")} />
          </button>
          {others && (
            <div className="mt-2 space-y-2 anim-pop">
              {alternatives.map((a) => <Button key={a.mode} block size="lg" icon={a.icon} onClick={() => switchMode(a.mode)}>{a.label}</Button>)}
            </div>
          )}
        </div>
      )}
      {!signupOpen && mode !== "forgot" && <p className="mt-6 text-center text-meta text-text-faint">Accounts are created by your workspace admin.</p>}
    </AuthShell>
  );
}

/** Landing page for the password reset email link: /reset-password?token=… */
export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const linkError = params.get("error");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (password !== confirm) { setError("The passwords don't match."); return; }
    setBusy(true); setError(null);
    try {
      const res = await authClient.resetPassword({ newPassword: password, token });
      if (res.error) setError(res.error.message ?? "Could not reset the password");
      else setDone(true);
    } finally { setBusy(false); }
  };

  return (
    <AuthShell>
      <h1 className="text-title font-semibold">{done ? "Password changed" : "Choose a new password"}</h1>
      {done ? (
        <>
          <p className="mt-1 text-body text-text-muted">You can now sign in with your new password.</p>
          <Link to="/login" className="mt-8 flex h-11 w-full items-center justify-center rounded-control bg-brand-strong text-body font-semibold text-white hover:brightness-110">Sign in</Link>
        </>
      ) : !token || linkError ? (
        <>
          <p className="mt-1 text-body text-text-muted">This reset link is invalid or has expired. Ask for a new one from the sign-in page.</p>
          <Link to="/login" className="mt-8 inline-flex h-10 items-center gap-1.5 text-body font-medium text-brand-text hover:underline"><ArrowLeft size={16} aria-hidden />Back to sign in</Link>
        </>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <Field label="New password" htmlFor="rp-pass" hint="At least 8 characters.">
            <Input id="rp-pass" autoFocus type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
          </Field>
          <Field label="Repeat new password" htmlFor="rp-confirm">
            <Input id="rp-confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
          </Field>
          {error && <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-body text-danger">{error}</p>}
          <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={busy || password.length < 8}>Change password</Button>
          <Link to="/login" className="inline-flex h-10 items-center gap-1.5 text-body font-medium text-text-muted hover:text-text"><ArrowLeft size={16} aria-hidden />Back to sign in</Link>
        </form>
      )}
    </AuthShell>
  );
}
